/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


const requestBox = document.querySelector('.request-box--wrap')

if ( requestBox != null ) {
    request(requestBox)
}

function request(requestBox) {
    const ticker = requestBox.querySelector('input[type="checkbox"]')
    const textBox = requestBox.querySelector('textarea')
    const linkedProduct = requestBox.querySelector('.product-linked')
    
    var addtocartBtn = document.querySelector(".product-info__add-button button[type='submit']");

    var textBoxValue = '';

    ticker?.addEventListener('change', function(event) {
        if (event.target.checked) {
            textBox.disabled = false;
            textBox.removeAttribute('disabled')
            textBox.focus()
            textBox.setAttribute('required', true)
            if ( linkedProduct != null ) {
                linkedProduct.value = linkedProduct.dataset.value;
            };

            if ( linkedProduct == null ) return;
            if (textBoxValue != '') {
                textBox.value = textBoxValue;
                textBoxValue = textBox.value;
            }
            if ( linkedProduct != null && textBox.value == '' && addtocartBtn != null) {
                addtocartBtn.setAttribute("disabled", "disabled")
            }
        } else {
            if (addtocartBtn != null) {
                addtocartBtn.hasAttribute("disabled") ? addtocartBtn.removeAttribute("disabled") : ''
            }
            textBox.setAttribute('disabled', true)
            textBox.removeAttribute('required')
            if ( linkedProduct != null ) {
                linkedProduct.value = '';
            }
            textBox.value = '';
        }
    })

    textBox?.addEventListener('input', function(event) {
        var addtocartBtn = document.querySelector(".product-info__add-button button[type='submit']");
        // Check if textarea has a value
        textBoxValue = event.target.value;
        event.target.value.trim() !== '' ? addtocartBtn.removeAttribute("disabled") : addtocartBtn.setAttribute("disabled", "disabled")
    });
}

if (document.querySelector('.hidden-message') && document.querySelector('[name="properties[Note]"]')) {
    document.querySelector('[name="properties[Note]"]').setAttribute('disabled','')
}

document.addEventListener('on:variant:change', (event) => {
    var detailForm = event.detail.form;
    var detailVariant = event.detail.variant;
    var detailProduct = event.detail.product;
    var addtocartBtn = document.querySelector(".product-info__add-button button[type='submit']");

    console.log(detailVariant.inventory_quantity)
    if ( event.detail.variant) {
        dataLayer.push({
            'event': 'display',
            'display_name': event.detail.variant.title + ' | ' + event.detail.variant.name
        });

        if (event.detail.variant.featured_image && event.detail.variant.featured_image.alt == "development-tba") {
            addtocartBtn?.setAttribute('disabled','')
        } else if (event.detail.variant.available){
            addtocartBtn?.removeAttribute('disabled')
        } 
    }

    if (gtag) {
        gtag('event', 'pdp_config', {
            event_category: 'Models',
            event_label: 'Product page',
            pdp_model_name: event.detail.variant.title + ' | ' + event.detail.variant.name,
            page_context: 'Product page' 
        });
    }

    document.querySelectorAll('.variant-message').forEach(m=> {
        if (m.dataset.variant == detailVariant.id) {
            m.removeAttribute('hidden')

            if (document.querySelector('.hidden-message')) {

                if (m.dataset.message != '') {
                    document.querySelector('.hidden-message').removeAttribute('hidden')
                    document.querySelector('.hidden-message').value = m.dataset.message
                } else {
                    document.querySelector('.hidden-message').setAttribute('hidden','')
                    document.querySelector('.hidden-message').value = ''
                }

                if (document.querySelector('[name="properties[Note]"]')) {
                    document.querySelector('[name="properties[Note]"]').setAttribute('disabled','')
                }
            }
        } else {
            m.setAttribute('hidden','')
        }
    })

    const data = document.getElementById('variantBoolean')?.textContent;
    const variants = JSON.parse(data || '[]');
    const currentVariantId = 123456789; // replace with your actual variant ID

    const hideNote = variants.find(v => v.variant_id === currentVariantId)?.hide_note;

    if (document.querySelector('[name="properties[Note]"]') && hideNote === true) {
        console.log('Show product note');
        document.querySelector('[name="properties[Note]"]').disabled = true;
    } else if (document.querySelector('[name="properties[Note]"]') && hideNote === false) {
        console.log('Show product note');
        document.querySelector('[name="properties[Note]"]').removeAttribute('disabled');
    }

    if (document.querySelector(`.variant-message[data-variant="${detailVariant.id}"]`) != null) {
        if (document.querySelector('[name="properties[Note]"]')) {
            document.querySelector('[name="properties[Note]"]').disabled = true;
        }
    } else {
        if (document.querySelector('[name="properties[Note]"]')) {
            document.querySelector('[name="properties[Note]"]').removeAttribute('disabled');
        }
    }

    if (document.querySelector('.product-info .price__from') != null) {
        document.querySelector('.product-info .price__from').style.display = 'none';
    }

    if (document.querySelector('.product-info .product-price') != null) {
        document.querySelector('.product-info .product-price').style.display = 'block';
        document.querySelector('.product-info .product-price').removeAttribute('hidden')
    }

    // if (detailVariant == undefined) {
    //     const unselectedOptions = Array.from(document.querySelectorAll('.variant-select-pickers .custom-select__option'))
    //     .filter(option => option.getAttribute('aria-selected') === 'false');

    //     console.log(unselectedOptions)
    //     // if (unselectedOptions.length > 0) {
    //     //     document.querySelector('.product-info .price__no-variant .price__current').textContent = unselectedOptions[0].dataset.value
    //     // }
    // }

    if (detailVariant == undefined) return;
    
    const backorderField = document.querySelector('.backorder-hidden')

    if (backorderField != null) {
        if ( detailVariant.inventory_management == 'shopify' ) {
            backorderField.value = ''
        } else {
            backorderField.value = backorderField.dataset.text
        }
    }
});

document.addEventListener('on:cart:add', (event) => {
    customize(event)
});

document.addEventListener('on:line-item:change', (event) => {
    console.log(event)
    customize(event)
});

function customize(event) {
    var productLinked = 0;
    var content = '';
    var qtyNext = event.detail.newQuantity
    productLinked = event.detail.cart.items.find(item => item.properties && item.properties['Addon Product']);
    content = event.detail.cart.items.find(item => item.properties && item.properties['Customize Your Dash']);

    console.log(productLinked)

    if (productLinked == undefined) return;

    let formData = ''
    var endpointLink = null
    if (event.type == "on:line-item:change") {
        var addOnLine = event.detail.cart.items.findIndex(item => item.id ? productLinked.properties['Addon Product'] : '');
        if (addOnLine == -1) return;

        endpointLink = window.Shopify.routes.root + 'cart/change.js';
        
        formData = {
            'line': addOnLine + 1,
            'quantity': qtyNext > 0 ? qtyNext : 0
        };

    } else if (event.type == "on:cart:add") {
        endpointLink = window.Shopify.routes.root + 'cart/add.js';
        formData = {
            'items': [{
             'id': productLinked.properties['Addon Product'],
             'quantity': qtyNext,
             'properties': {
                'Content': productLinked.properties['Customize Your Dash'] ? productLinked.properties['Customize Your Dash'] : '',
                'Linked To': productLinked.title
              }
             }]
           };
    }

    fetch(endpointLink, {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
    })
    .then(response => {
        return response.json();
    })
    .then(response => {
        setTimeout( function() {
            document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                bubbles: true
            }));
        },500)
    })
    .catch((error) => {
        console.error('Error:', error);
    });
}


document.addEventListener('line-remove', (e)=> {
    const addonLine = e.detail.el.getAttribute('data-addon-line');
    const addonCartId = e.detail.el.dataset.addonId;
    
    var mainTitle = e.detail.el.dataset.title
    const addonCartLine =  e.detail.cart.items.findIndex(item => item.properties && item.properties['Linked To'] === mainTitle);
    
    if (addonCartLine != -1) {
        formData = {
            'line': addonCartLine == 0 ? addonCartLine + 1 : addonCartLine,
            'quantity': 0
        };
        fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData)
        })
        .then(response => {
            return response.json();
        })
        .then(response => {
            setTimeout( function() {
                document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                    bubbles: true
                }));
            },500)
        })
    }
})

class CustomizationLink extends HTMLElement {
    constructor() {
      super();
      this.productText = this.dataset.productTitle;
      this.content = this.dataset.content;
      this.line = this.dataset.line;
      this.productId = this.dataset.productId;
      this.qty = this.dataset.qty;
      this.addonLine = this.dataset.addonLine
      this.addonQty = this.dataset.addonQty
      this.addonId = this.dataset.addonId

    //   this.clickEvent = new Event('click', { bubbles: true });
    }
    connectedCallback() {
        this.addEventListener('click', this.onButtonClick.bind(this));
    }
    onButtonClick(event) {
        event.preventDefault();

        fetch(
            `${window.Shopify.routes.root}?section_id=customization-popup`
          )
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const source = html.querySelector('modal-dialog')
            
              document.body.prepend(source)

              document.querySelector('#modal-custom h3').textContent = this.productText;
              document.querySelector('#modal-custom textarea').value = this.content;
              document.querySelector('#modal-custom textarea').focus()


                document.querySelector('.update-cus').addEventListener('click', async function(e) {
                    
                    await fetch("".concat(window.Shopify.routes.root + 'cart/change.js'), {
                        body: JSON.stringify({
                            line: event.target.line,
                            quantity: event.target.qty,
                            properties: {
                                'Customise Your Dash': document.querySelector('#modal-custom textarea').value,
                                'Addon Product': event.target.addonId
                            }
                        }),
                        credentials: "same-origin",
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest"
                        }
                    })
                    const textValue = document.querySelector('#modal-custom textarea').value.trim()

                    await fetch("".concat(window.Shopify.routes.root + 'cart/change.js'), {
                        body: JSON.stringify({
                            line: event.target.addonLine,
                            quantity: textValue == '' ? 0 : event.target.qty,
                            properties: {
                                'Customise Your Dash': document.querySelector('#modal-custom textarea').value
                            }
                        }),
                        credentials: "same-origin",
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest"
                        }
                    }).then((function(cart) {
                        
                        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                            bubbles: true
                        }));
                        document.querySelector('#modal-custom .js-close-modal').click()
                    }))
                })

                document.querySelector('.remove-cus').addEventListener('click', async function(e) {
                    await fetch("".concat(window.Shopify.routes.root + 'cart/change.js'), {
                        body: JSON.stringify({
                            line: event.target.line,
                            quantity: event.target.qty,
                            properties: {
                                'Customise Your Dash': '',
                                'Addon Product': event.target.addonId
                            }
                        }),
                        credentials: "same-origin",
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest"
                        }
                    }).then((function(cart) {
                    }))

                    var addQuant = event.target.addonQty - event.target.qty

                    console.log(document.querySelector('#modal-custom textarea').value.trim())

                    if ( document.querySelector('#modal-custom textarea').value.trim() == '') {
                        addQuant = 0;
                    }

                    await fetch("".concat(window.Shopify.routes.root + 'cart/change.js'), {

                        body: JSON.stringify({
                            line: event.target.addonLine,
                            quantity: addQuant <= 0 ? 0 : addQuant,
                            properties: {
                                'Customise Your Dash': document.querySelector('#modal-custom textarea').value
                            }
                        }),
                        credentials: "same-origin",
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest"
                        }
                    }).then((function(cart) {
                        document.querySelector('#modal-custom .js-close-modal').click()
                        
                        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                          bubbles: true
                        }));
                    }))
                })
            });
    }
}

// Define the custom element
customElements.define('customization-link', CustomizationLink);


// // Disable right-click
// window.addEventListener('contextmenu', function (e) {
//     e.preventDefault();
// });

// // Disable text highlighting
// window.addEventListener('mousedown', function (e) {
//     e.preventDefault();
// });

document.querySelectorAll('.custom-select__option').forEach(e=> {
    e.addEventListener('click', function() {
        var text = e.querySelector('span').textContent
        var parent = e.closest('.custom-select')
        var btn_label = parent.querySelector('span')
 
        if ( text.includes('No display/custom') || text.includes('No display/bare') || text.includes('Round analog-style gauges')) {
            btn_label.textContent = text
        }
    })
})




// Function to trigger hover effect on an element
function triggerHover(element) {
    // Create a new MouseEvent of type 'mouseenter'
    const mouseEnterEvent = new MouseEvent('mouseenter', {
      bubbles: true,
      cancelable: true,
      view: window
    });
  
    // Dispatch the 'mouseenter' event on the element
    element.dispatchEvent(mouseEnterEvent);
  
    // Create a new MouseEvent of type 'mouseleave'
    const mouseLeaveEvent = new MouseEvent('mouseleave', {
      bubbles: true,
      cancelable: true,
      view: window
    });
  
    // Dispatch the 'mouseleave' event on the element after a short delay (optional)
    setTimeout(() => {
      element.dispatchEvent(mouseLeaveEvent);
    }, 500); // Delay of 500ms (half a second)
}

document.addEventListener('DOMContentLoaded', function() {
    document.querySelector('.btn--share-pop')?.addEventListener('click', function() {
        var parent = document.querySelector('.button--share-wrap');
        parent.classList.toggle('active');
    });

    document.querySelector('.button--share-wrap')?.addEventListener('mouseleave', function(e) {
        if (e.currentTarget.classList.contains('active')) {
            e.currentTarget.classList.remove('active')
        }
    });
});


if ( document.querySelector('.request-box--wrap') != null ) {
    document.querySelector('.tab-content#tab1').insertAdjacentElement('beforeEnd', document.querySelector('.request-box--wrap'))
}

if (document.querySelector('.product-info__block.vivid-box') != null) {
    document.querySelector('.tab-content#tab1').insertAdjacentElement('beforeEnd', document.querySelector('.product-info__block.vivid-box'))
}

if (document.querySelector('.in-the-box') != null && document.querySelector('.in-box-append') != null ) {
    document.querySelector('.in-box-append').insertAdjacentElement('beforeEnd', document.querySelector('.in-the-box'))
} 

if (document.querySelector('.disclaimer-text') != null && document.querySelector('.disclaimer-append') != null ) {
    document.querySelector('.disclaimer-append').insertAdjacentElement('beforeEnd', document.querySelector('.disclaimer-text'))
}

// const wlhAppnder = setInterval( function() {
//     if (document.querySelector('.header-wish .wishlisthero-header-block') == null) {
//         document.querySelector('.header-wish').appendChild(document.querySelector('.wishlisthero-header-block'))
//     } else {
//         clearInterval(wlhAppnder);
//     }
// },100)


document.addEventListener('on:facet-filters:updated',function(event) {
    console.log('filter updated')

    setTimeout( function() {

        const makeTicks = document.querySelectorAll('[data-filter-label="make"]');
        const checkedMakes = Array.from(makeTicks).filter(e => e.checked);
        
        if (checkedMakes.length == 0) {
            document.querySelector('.filter-model')?.setAttribute('hidden','')
            document.querySelector('.make-disclosure')?.setAttribute('open','')
        }

        document.querySelectorAll('.facets__filters .checkbox:checked').forEach(e=> {
            if (e.hasAttribute('data-one')) {
                document.querySelectorAll('.main-products-grid__results product-card')[0].querySelector('a').click()
            }
        })

        console.log(document.querySelector('.single-make'))
        
        if (checkedMakes.length == 1) {
            const checkedMakeValues = checkedMakes.map(e => e.value);
            document.querySelector('.single-make').textContent = checkedMakeValues[0]
        } else {
            document.querySelector('.single-make').textContent = document.querySelector('.single-make').dataset.text
        }
    },200)
});


document.addEventListener('click', function(e) {
    if (e.target.id != undefined && e.target.id == 'ymm_search') {
        console.log(document.getElementById('dropdown-field_2').options[document.getElementById('dropdown-field_2').selectedIndex].textContent)
        console.log(document.getElementById('dropdown-field_3').options[document.getElementById('dropdown-field_3').selectedIndex].textContent)
        console.log(document.getElementById('dropdown-field_1').options[document.getElementById('dropdown-field_1').selectedIndex].textContent)

        gtag("event", "model_search", {
            "make": document.getElementById('dropdown-field_2').options[document.getElementById('dropdown-field_2').selectedIndex].textContent,
            "model": document.getElementById('dropdown-field_3').options[document.getElementById('dropdown-field_3').selectedIndex].textContent,
            "year": document.getElementById('dropdown-field_1').options[document.getElementById('dropdown-field_1').selectedIndex].textContent
        });
    }
})

document.querySelectorAll('a[href="#whatsapp"]').forEach(e=> {
    e.addEventListener('click', function(evt) {
        evt.preventDefault()
        // jQNC(".csaas-whatsapp-form-button").trigger("click");
    })
})

if (document.querySelector('.custom-variant-picker') != null) {
    setTimeout( function() { 
        document.querySelector('.media-thumbs__item:first-child .media-thumbs__btn')?.click()
    },100)
}

// js/common/behavior/scroll-progress.js
var ScrollProgress = class extends HTMLElement {
    connectedCallback() {
      this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
      if (window.ResizeObserver) {
        new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
      }
    }
    get scrolledElement() {
      return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
    }
    _updateScrollProgress() {
      const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft), advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
      this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
    }
  };
if (!window.customElements.get("scroll-progress")) {
    window.customElements.define("scroll-progress", ScrollProgress);
}


function throttle(fn, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = new Date().getTime();
      if (now - lastCall < delay) {
        return;
      }
      lastCall = now;
      return fn(...args);
    };
}

const header = document.querySelector('.cc-header--sticky');
if (header) {
  const observer = new IntersectionObserver(
    ([e]) => header.classList.toggle('is-stuck', !e.isIntersecting),
    { threshold: 1 }
  );
  observer.observe(header);
}